import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Rating,
  IconButton,
  TextField,
  Alert,
  Skeleton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ButtonGroup,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart,
  FavoriteBorder,
  Favorite,
  Share,
  LocalShipping,
  Security,
  NavigateNext,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { fetchProductById, clearCurrentProduct } from '../../store/slices/productSlice';
import { addToCart, addToLocalCart } from '../../store/slices/cartSlice';
import { RootState } from '../../types';
import { AppDispatch } from '../../store/store';
import { toast } from 'react-toastify';
import BreadcrumbJsonLd from '../../components/seo/BreadcrumbJsonLd';
import PriceDisplay from '../../components/common/PriceDisplay';
import ReviewSummary from '../../components/reviews/ReviewSummary';
import ReviewList from '../../components/reviews/ReviewList';
import StarRating from '../../components/reviews/StarRating';
import { getImageUrl } from '../../utils/imageUtils';
import { DEFAULT_BGN_TO_EUR_RATE } from '../../utils/currency';
import analytics from '../../utils/analytics';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { currentProduct: product, loading, error } = useSelector((state: RootState) => state.products);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [quantity, setQuantity] = useState<number>(1);
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [imageZoomOpen, setImageZoomOpen] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(Number(id)));
    }

    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (product) {
      analytics.viewItem(product);
    }
  }, [product]);

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 1)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    if (isAuthenticated) {
      // Use backend API for authenticated users
      dispatch(addToCart({ productId: product.id, quantity }));
    } else {
      // Use local sessionStorage for guest users
      dispatch(addToLocalCart({ product, quantity }));
    }
    analytics.addToCart(product, quantity);
  };

  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      toast.info('Please login to add favorites');
      navigate('/login');
      return;
    }
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton height={40} />
            <Skeleton height={20} width="60%" />
            <Skeleton height={30} width="40%" />
            <Box sx={{ my: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={20} sx={{ mb: 1 }} />
              ))}
            </Box>
            <Skeleton variant="rectangular" height={60} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Product not found'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/shop')}>
          Back to Shop
        </Button>
      </Container>
    );
  }

  // Get product images with fallback to legacy imageUrl or placeholder
  const productImages = product.images && product.images.length > 0
    ? product.images.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return a.displayOrder - b.displayOrder;
      })
    : [];
  
  const images = productImages.length > 0
    ? productImages.map(img => getImageUrl(img.fileUrl))
    : [getImageUrl(product.imageUrl)];
  
  const currentImage = images[selectedImage] || '/placeholder-book.svg';
  
  const handleImageZoom = () => {
    setImageZoomOpen(true);
  };
  
  const handleImageZoomClose = () => {
    setImageZoomOpen(false);
  };
  const inStock = product.stock > 0;

  // Generate Book Schema markup
  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": product.title,
    "author": product.author ? {
      "@type": "Person",
      "name": product.author
    } : undefined,
    "isbn": product.isbn || undefined,
    "publisher": product.publisher ? {
      "@type": "Organization",
      "name": product.publisher
    } : undefined,
    "datePublished": product.publicationDate || undefined,
    "numberOfPages": product.pages || undefined,
    "inLanguage": product.language || "bg",
    "image": currentImage,
    "description": product.description,
    "aggregateRating": product.reviewCount && product.reviewCount > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": product.averageRating || 0,
      "reviewCount": product.reviewCount
    } : undefined,
    "offers": {
      "@type": "Offer",
      "price": ((product.discountPrice || product.price || 0) / DEFAULT_BGN_TO_EUR_RATE).toFixed(2),
      "priceCurrency": "EUR",
      "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": window.location.href,
      "seller": {
        "@type": "Organization",
        "name": "Golden Books"
      }
    }
  };

  // Remove undefined values
  const cleanSchema = JSON.parse(JSON.stringify(bookSchema));

  // Generate meta description
  const metaDescription = product.description
    ? product.description.substring(0, 155) + (product.description.length > 155 ? '...' : '')
    : `${product.title} от ${product.author || 'Golden Books'}. Купете онлайн от Golden Books - специализиран магазин за хомеопатична литература.`;

  const pageTitle = `${product.title}${product.author ? ' - ' + product.author : ''} | Golden Books`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${product.title}, ${product.author || ''}, хомеопатия, книги, хомеопатична литература, ${product.categories?.[0]?.name || ''}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="book" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={currentImage} />
        {product.isbn && <meta property="book:isbn" content={product.isbn} />}
        {product.author && <meta property="book:author" content={product.author} />}
        {product.publicationDate && <meta property="book:release_date" content={product.publicationDate} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={currentImage} />

        {/* Book Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(cleanSchema)}
        </script>
      </Helmet>

      <BreadcrumbJsonLd
        items={[
          { name: t('common.home'), url: '/' },
          { name: t('common.shop'), url: '/shop' },
          ...(product.categories?.[0]
            ? [{ name: product.categories[0].name, url: `/shop?category=${product.categories[0].name.toLowerCase()}` }]
            : []),
          { name: product.title },
        ]}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          {t('common.home')}
        </Link>
        <Link
          color="inherit"
          href="/shop"
          onClick={(e) => {
            e.preventDefault();
            navigate('/shop');
          }}
        >
          {t('common.shop')}
        </Link>
        {product.categories?.[0] && (
          <Link
            color="inherit"
            href={`/shop?category=${product.categories[0]?.name.toLowerCase()}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/shop?category=${product.categories?.[0]?.name.toLowerCase()}`);
            }}
          >
            {product.categories[0]?.name}
          </Link>
        )}
        <Typography color="text.primary">{product.title}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <img
                src={currentImage}
                alt={productImages[selectedImage]?.altText || product.title}
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  cursor: 'zoom-in',
                }}
                onClick={handleImageZoom}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-book.svg';
                }}
              />
              <IconButton
                onClick={handleImageZoom}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>
              {productImages[selectedImage]?.isPrimary && (
                <Chip
                  label="Primary"
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                  }}
                />
              )}
            </Box>
            {images.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={image}
                      alt={productImages[index]?.altText || `${product.title} ${index + 1}`}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: selectedImage === index ? '3px solid #1976d2' : '3px solid transparent',
                        transition: 'border-color 0.2s ease',
                      }}
                      onClick={() => setSelectedImage(index)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-book.svg';
                      }}
                    />
                    {productImages[index]?.isPrimary && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          bottom: 2,
                          left: 2,
                          fontSize: '0.6rem',
                          height: 16,
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Box>
            {/* Title and Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.title}
              </Typography>
              <Box>
                <IconButton onClick={handleToggleFavorite}>
                  {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                </IconButton>
                <IconButton onClick={handleShare}>
                  <Share />
                </IconButton>
              </Box>
            </Box>

            {/* Author */}
            {product.author && (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('product.author')}: {product.author}
              </Typography>
            )}

            {/* Rating and Reviews */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StarRating rating={product.averageRating || 0} size="medium" />
              <Typography variant="body2" sx={{ ml: 1 }}>
                ({product.reviewCount || 0} {t('product.reviews')})
              </Typography>
            </Box>

            {/* Price */}
            <PriceDisplay
              price={product.price}
              discountPrice={product.discountPrice}
              priceEur={product.priceEur}
              discountPriceEur={product.discountPriceEur}
              variant="h4"
              layout="stacked"
              sx={{ mb: 2 }}
            />

            {/* Category */}
            {product.categories?.[0] && (
              <Chip
                label={product.categories[0]?.name}
                color="primary"
                variant="outlined"
                sx={{ mb: 2 }}
              />
            )}

            {/* Stock Status */}
            <Box sx={{ mb: 3 }}>
              {inStock ? (
                <Typography variant="body2" color="success.main">
                  ✓ {t('product.inStock')} ({product.stock} в наличност)
                </Typography>
              ) : (
                <Typography variant="body2" color="error.main">
                  ✗ {t('product.outOfStock')}
                </Typography>
              )}
            </Box>

            {/* Quantity and Add to Cart */}
            {inStock && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  {t('cart.quantity')}:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <ButtonGroup size="small">
                    <IconButton onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>
                      <RemoveIcon />
                    </IconButton>
                    <TextField
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= 1 && value <= product.stock) {
                          setQuantity(value);
                        }
                      }}
                      inputProps={{
                        style: { textAlign: 'center', width: '60px' },
                        min: 1,
                        max: product.stock,
                      }}
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= product.stock}
                    >
                      <AddIcon />
                    </IconButton>
                  </ButtonGroup>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCart />}
                    onClick={handleAddToCart}
                    sx={{ flex: 1 }}
                  >
                    {t('product.addToCart')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Product Features */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('home.features.freeShipping')}
                </Typography>
                <List dense>
                  <ListItem>
                    <LocalShipping sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText primary={t('home.features.freeShippingDesc')} />
                  </ListItem>
                  <ListItem>
                    <Security sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText primary={t('home.features.securePaymentDesc')} />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Product Information Tabs */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          {t('product.description')}
        </Typography>
        
        <Grid container spacing={4}>
          {/* Description */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('product.description')}
              </Typography>
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>
            </Paper>
          </Grid>

          {/* Specifications */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('product.specifications')}
              </Typography>
              <Box sx={{ '& > *': { mb: 1 } }}>
                {product.isbn && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('product.isbn')}:
                    </Typography>
                    <Typography variant="body2">{product.isbn}</Typography>
                  </Box>
                )}
                {product.publisher && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('product.publisher')}:
                    </Typography>
                    <Typography variant="body2">{product.publisher}</Typography>
                  </Box>
                )}
                {product.publicationDate && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('product.publicationDate')}:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(product.publicationDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                {product.pages && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('product.pages')}:
                    </Typography>
                    <Typography variant="body2">{product.pages}</Typography>
                  </Box>
                )}
                {product.language && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('product.language')}:
                    </Typography>
                    <Typography variant="body2">{product.language}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Reviews Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('reviews.customerReviews')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {/* Review Summary */}
        <ReviewSummary
          productId={Number(id)}
          productTitle={product.title}
          onReviewAdded={() => {
            // Refresh product to update review count
            dispatch(fetchProductById(Number(id)));
          }}
        />
        
        {/* Review List */}
        <ReviewList
          productId={Number(id)}
          refreshTrigger={product.reviewCount}
        />
      </Box>

      {/* Image Zoom Modal */}
      {imageZoomOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
          onClick={handleImageZoomClose}
        >
          <IconButton
            onClick={handleImageZoomClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <img
              src={currentImage}
              alt={productImages[selectedImage]?.altText || product.title}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-book.svg';
              }}
            />
            {images.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', maxWidth: '100%' }}>
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={productImages[index]?.altText || `${product.title} ${index + 1}`}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: selectedImage === index ? '3px solid #1976d2' : '3px solid rgba(255, 255, 255, 0.3)',
                      opacity: selectedImage === index ? 1 : 0.7,
                      transition: 'all 0.2s ease',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(index);
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-book.svg';
                    }}
                  />
                ))}
              </Box>
            )}
            {productImages[selectedImage] && (
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  textAlign: 'center',
                  maxWidth: '80%',
                }}
              >
                {productImages[selectedImage].altText || productImages[selectedImage].fileName}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Container>
    </>
  );
};

export default ProductDetailPage;